/**
 * MethodComparisonPanel — fully dynamic comparison panel.
 *
 * Renders the selected (source) method total on one side and the chosen target
 * method total on the other, with a savings indicator and a "Use [target]" button.
 * Works for ANY source/target method pair. When the target is a sheet-based method
 * (Screen Print Pro / UltraColour Reflective), a sheet-setup input block is shown.
 */
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, TrendingDown, TrendingUp, Equal, CheckCircle2, AlertCircle, Grid3X3 } from "lucide-react";
import { SHEET_SIZES } from "./constants";
import MethodInputFields from "./MethodInputFields";

const fmt = (v) => (v === null || v === undefined ? "$0.00" : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(v));

function Lines({ lines }) {
  return (
    <div className="space-y-1">
      {(lines || []).map((l, i) => (
        <div key={i} className="flex justify-between text-sm gap-3">
          <span className="text-slate-500 truncate">{l.label}</span>
          <span className="font-mono shrink-0">{l.money ? fmt(l.value) : l.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function MethodComparisonPanel({ comparison, inputs = {}, onInputsChange, targetConfig = {}, onTargetChange, onUse }) {
  if (!comparison) return null;
  const { source_method, target_method, eligible } = comparison;
  const targetIsSheet = comparison.target_is_sheet;
  const set = (field, value) => onInputsChange({ ...inputs, [field]: value });
  const targetCheaper = comparison.target_is_cheaper;
  const savingsPercent = comparison.savings_percent || 0;

  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white" data-testid="method-comparison-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-indigo-900">
          <Layers className="w-5 h-5 text-indigo-600" />
          {source_method} vs {target_method} Comparison
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Compares your selected method against {target_method}. This does not change your quote until you click "Use {target_method}".
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Target method inputs — rendered from the SAME MethodInputFields source used by the primary print group */}
        {onTargetChange && (
          <MethodInputFields
            method={target_method}
            group={targetConfig}
            onUpdate={(f, v) => onTargetChange({ ...targetConfig, [f]: v })}
            onUpdateFields={(fields) => onTargetChange({ ...targetConfig, ...fields })}
            index="cmp"
            context="comparison"
            totalUnits={comparison.total_units || 0}
          />
        )}
        {false && targetIsSheet && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Grid3X3 className="w-4 h-4 text-gray-500" />
              <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">{target_method} Sheet Setup</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Garments per Sheet</Label>
                <Input type="number" min="1" max="50" value={inputs.garments_per_sheet || 1}
                  onChange={(e) => set("garments_per_sheet", Math.max(1, parseInt(e.target.value) || 1))}
                  className="h-9 font-mono" data-testid="cmp-garments-per-sheet" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Colours</Label>
                <Input type="number" min="1" max="6" value={inputs.colour_count || 1}
                  onChange={(e) => set("colour_count", Math.min(6, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="h-9 font-mono" data-testid="cmp-colour-count" />
              </div>
              {target_method === "Screen Print Pro" && (
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Sheet Size</Label>
                  <Select value={inputs.sheet_size || "Standard"} onValueChange={(v) => set("sheet_size", v)}>
                    <SelectTrigger className="h-9" data-testid="cmp-sheet-size"><SelectValue /></SelectTrigger>
                    <SelectContent>{SHEET_SIZES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch id="cmp-lod" checked={inputs.light_on_dark || false} onCheckedChange={(c) => set("light_on_dark", c)} data-testid="cmp-light-dark" />
              <Label htmlFor="cmp-lod" className="text-sm cursor-pointer">Light on Dark</Label>
            </div>
          </div>
        )}

        {/* Result */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Comparison Result</span>
          </div>
          {!eligible ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">{target_method} not available</p>
                <p className="text-sm text-amber-700 mt-1">{comparison.reason}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className={`rounded-lg border p-3 text-center ${!targetCheaper ? "bg-green-50 border-green-300" : "bg-white"}`}>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1 truncate">{source_method}</p>
                  <p className={`font-mono font-bold text-lg ${!targetCheaper ? "text-green-700" : ""}`} data-testid="cmp-source-total">{fmt(comparison.source_total)}</p>
                </div>
                <div className="bg-white rounded-lg border p-3 flex flex-col items-center justify-center">
                  {targetCheaper ? <TrendingDown className="w-5 h-5 text-green-600 mb-1" /> : comparison.difference < 0 ? <TrendingUp className="w-5 h-5 text-red-500 mb-1" /> : <Equal className="w-5 h-5 text-gray-400 mb-1" />}
                  <p className={`font-mono text-sm font-medium ${targetCheaper ? "text-green-600" : comparison.difference < 0 ? "text-red-500" : "text-gray-500"}`}>
                    {comparison.difference > 0 ? "Save " : ""}{fmt(Math.abs(comparison.difference || 0))}
                  </p>
                </div>
                <div className={`rounded-lg border p-3 text-center ${targetCheaper ? "bg-green-50 border-green-300" : "bg-white"}`}>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1 truncate">{target_method}</p>
                  <p className={`font-mono font-bold text-lg ${targetCheaper ? "text-green-700" : ""}`} data-testid="cmp-target-total">{fmt(comparison.target_total)}</p>
                </div>
              </div>
              {savingsPercent > 0 && (
                <div className="flex justify-center">
                  <Badge className="bg-green-600 text-white text-sm px-3 py-1">
                    Save {savingsPercent}% with {targetCheaper ? target_method : source_method}
                  </Badge>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">{source_method} Breakdown</p>
                  <Lines lines={comparison.source_lines} />
                  <div className="flex justify-between pt-2 mt-1 border-t border-slate-300 font-bold text-sm">
                    <span className="text-slate-700">Total</span><span className="font-mono">{fmt(comparison.source_total)}</span>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">{target_method} Breakdown</p>
                  <Lines lines={comparison.target_lines} />
                  <div className="flex justify-between pt-2 mt-1 border-t border-slate-300 font-bold text-sm">
                    <span className="text-slate-700">Total</span><span className="font-mono">{fmt(comparison.target_total)}</span>
                  </div>
                </div>
              </div>
              {onUse && (
                <Button onClick={onUse} className={`w-full ${targetCheaper ? "bg-green-600 hover:bg-green-700" : "bg-indigo-600 hover:bg-indigo-700"} text-white`} data-testid="cmp-use-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Use {target_method}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
